import React from 'react';

import { NEW_FEATURE_DATA_ATTRIBUTE } from 'app/constants';
import IconAlerts from 'app/ui-kit/icons/Alerts.svg';
import IconApiReference from 'app/ui-kit/icons/ApiReference.svg';
import IconDashboard from 'app/ui-kit/icons/Dashboard.svg';
import IconScanner from 'app/ui-kit/icons/Scanner.svg';
import Settings from 'app/ui-kit/icons/Settings.svg';
import IconSources from 'app/ui-kit/icons/Sources.svg';
import IconSupport from 'app/ui-kit/icons/Support.svg';
import waitScriptReady from 'utils/wait-for-script-ready';

interface Route {
    key: string;
    link: string | undefined;
    onClick?: () => void;
    pattern?: RegExp;
    text?: string;
    icon?: React.ComponentType;
    virtual?: boolean;
    divider?: 'top' | 'bottom' | 'both';
    [NEW_FEATURE_DATA_ATTRIBUTE]?: string;
    children?: Route[];
}

export const menuRoutes: Route[] = [
    {
        key: 'search',
        link: '/search',
        text: 'Catalog',
        icon: IconDashboard,
        children: [
            {
                key: 'search_vendors',
                link: '/search/vendors',
                text: 'Vendors',
                pattern: new RegExp('/search/vendors.*')
            },
            {
                key: 'search_products',
                link: '/search/products',
                text: 'Products',
                pattern: new RegExp('/search/products.*')
            },
            {
                key: 'search_by_years',
                link: '/search/years',
                text: 'Timeline',
                pattern: new RegExp('/search/years.*')
            },
            {
                key: 'search_by_cvss',
                link: '/search/cvss',
                text: 'Vulnerabilities',
                pattern: new RegExp('/search/cvss.*')
            },
            {
                key: 'search_tags',
                link: '/search/tags',
                pattern: new RegExp('/search/tags.*'),
                virtual: true
            }
        ]
    },
    {
        key: 'sources',
        link: '/stats',
        text: 'Sources',
        icon: IconSources,
        children: [
            {
                key: 'sources_docs',
                link: 'https://docs.vulners.com',
                text: 'Documentation'
            },
            {
                key: 'sources_blog',
                link: 'https://vulners.com/blog',
                text: 'Blog'
            },
            {
                key: 'sources_glossary',
                link: 'https://vulners.com/glossary',
                text: 'Glossary'
            },
            {
                key: 'sources_faq',
                link: '/faq',
                text: 'FAQ'
            }
        ]
    },
    {
        key: 'assessment',
        link: '/scanner',
        text: 'Assessment',
        icon: IconScanner,
        children: [
            {
                key: 'assessment_scanner',
                link: '/scanner',
                text: 'Agents dashboard',
                children: [
                    {
                        key: 'assessment_scanner_scan',
                        link: '/scanner/scan',
                        text: 'Agent Scanning'
                    },
                    {
                        key: 'assessment_scanner_api_scan',
                        link: '/scanner/apiscan',
                        text: 'API Scanning'
                    },
                    {
                        key: 'assessment_scanner_audit',
                        link: '/scanner/audit',
                        text: 'Manual Audit'
                    }
                ]
            },
            {
                key: 'assessment_perimeter_control',
                link: '/vscanner/hosts',
                text: 'Perimeter control',
                children: [
                    {
                        key: 'assessment_perimeter_control_scan',
                        link: '/vscanner/scan',
                        text: 'Scanning'
                    },
                    {
                        key: 'assessment_perimeter_control_projects',
                        link: '/vscanner/projects',
                        text: 'Projects'
                    },
                    {
                        key: 'assessment_perimeter_control_dashboard_hosts',
                        link: '/vscanner/hosts',
                        virtual: true
                    },
                    {
                        key: 'assessment_perimeter_control_dashboard_network',
                        link: '/vscanner/network',
                        virtual: true
                    },
                    {
                        key: 'assessment_perimeter_control_dashboard_web',
                        link: '/vscanner/web',
                        virtual: true
                    }
                ]
            },
            {
                key: 'vscanner_dashboard_network',
                link: 'https://docs.vulners.com/docs/products/assessment/',
                text: 'Assessment API'
            }
        ]
    },
    {
        key: 'alerts',
        link: '/email',
        text: 'Alerts',
        icon: IconAlerts,
        children: [
            {
                key: 'alerts_email',
                link: '/email',
                text: 'Email notifications'
            },
            {
                key: 'alerts_webhook',
                link: '/webhook',
                text: 'Webhook notifications'
            },
            {
                key: 'alerts_api',
                link: 'https://docs.vulners.com/docs/products/alerts/',
                text: 'Alerts API'
            }
        ]
    },
    {
        key: 'api_reference',
        link: 'https://docs.vulners.com/docs/api/',
        text: 'API Reference',
        icon: IconApiReference
    },
    {
        key: 'support',
        text: 'Support',
        icon: IconSupport,
        link: undefined,
        onClick: () => {
            waitScriptReady('ze-snippet').then(() => {
                window.zE?.('webWidget', 'show');
                window.zE?.('webWidget', 'open');
            });
        }
    },
    {
        key: 'settings',
        link: '/userinfo',
        text: 'Settings',
        icon: Settings,
        [NEW_FEATURE_DATA_ATTRIBUTE]: 'setting-menu-item',
        divider: 'top'
    }
];

export function getRouteInfoByLink(link: string) {
    link = link.replace(/\/page\/\d+/g, '');
    let done = false;
    const output: { active: string | null; expanded: string[] } = { active: null, expanded: [] };

    const inner = (node: Route, path: string[] = []) => {
        switch (true) {
            case done:
                return;
            case node.pattern?.test(link):
            case node.link === link:
                if (node.virtual) {
                    output.active = path[path.length - 1];
                } else {
                    output.active = node.key;
                    if (link.includes('email')) {
                        console.log({ output });
                    }
                }
                output.expanded = [...path, node.key];
                node.children?.forEach((child) => inner(child, [...path, node.key]));
                done = true;
                return;
            case Array.isArray(node.children):
                node.children?.forEach((child) => inner(child, [...path, node.key]));
                return;
            default:
                return;
        }
    };

    menuRoutes.forEach((node) => inner(node));
    return output;
}
